import React, { FC } from "react";
import { useFormContext } from "react-hook-form";

import { Loading, Typography } from "ui-library/atoms";
import { ContentBox } from "ui-library/layouts";
import { LabelTextItem } from "ui-library/modules";

import i18n from "core/i18n";
import { localFullDate } from "core/formatters";
import { getLocalizedOneLineAddressFull } from "core/localized";
import { Attachment, CancellationReason, VehicleCancellationReason } from "types";

import { formatAttachments, formatBankAccount, formatPhoneAndEmail, formatSubjectData, getCancellationDateTitle } from "../../util";
import { VehicleCancellationFormValues } from "../../types";

type StepOneProps = {
    cancellationReasons: CancellationReason[];
    attachments: Attachment[];
    isAttachmentsFetching: boolean;
};

export const Recapitulation: FC<StepOneProps> = ({ cancellationReasons, attachments, isAttachmentsFetching }) => {
    const { t } = i18n.useTranslation();

    const { getValues } = useFormContext<VehicleCancellationFormValues>();

    const [cancellationReason, cancellationDate] = getValues(["cancellationReason", "cancellationDate"]);
    const [cancellationNote, contactPerson] = getValues(["cancellationNote", "contactPerson"]);
    const [fundsTransfer, fundsTransferBankAccount] = getValues(["fundsTransfer", "fundsTransferBankAccount"]);

    const mandatoryAttachments = attachments?.filter((attachment: Attachment) => !attachment.isOptional);
    const optionalAttachments = attachments?.filter((attachment: Attachment) => attachment.isOptional);

    const selectedCancellationReasonData = cancellationReasons.find((item) => item.code === cancellationReason);

    return (
        <>
            <ContentBox fullWidth textAlign={"center"}>
                <Typography variant="h3">{t("vehicle.change.contractCancellation.changelist")}</Typography>
            </ContentBox>

            <ContentBox fullWidth>
                {/* Cancellation reason */}
                <LabelTextItem
                    label={t("vehicle.change.contractCancellation.cancellationReason")}
                    text={selectedCancellationReasonData?.label}
                />

                {/* Cancellation date */}
                <LabelTextItem label={getCancellationDateTitle(t, cancellationReason)} text={localFullDate(cancellationDate)} />

                {/* Agreement - cancellation note */}
                {cancellationReason === VehicleCancellationReason.BY_AGREEMENT && (
                    <LabelTextItem label={t("common.note")} text={cancellationNote} />
                )}

                {/* Insurer death - new contract person */}
                {cancellationReason === VehicleCancellationReason.INSURER_DEATH && (
                    <>
                        <LabelTextItem
                            label={t("vehicle.change.contractCancellation.recap.newContact")}
                            text={formatSubjectData(t, contactPerson)}
                        />
                        <LabelTextItem
                            label={t("vehicle.change.contractCancellation.recap.newContactEmailPhone")}
                            text={formatPhoneAndEmail(t, contactPerson)}
                        />
                        <LabelTextItem
                            label={t("vehicle.change.contractCancellation.recap.newContactAddress")}
                            text={getLocalizedOneLineAddressFull(t, contactPerson.address)}
                        />
                    </>
                )}

                {/* Funds transfer */}
                <LabelTextItem
                    label={t("change.fundsTransfer.account")}
                    text={formatBankAccount(t, fundsTransfer, fundsTransferBankAccount)}
                />

                <Loading loading={isAttachmentsFetching}>
                    {/* Mandatory Attachments */}
                    {mandatoryAttachments.length > 0 && (
                        <LabelTextItem label={t("change.mandatoryAttachments")} text={formatAttachments(mandatoryAttachments)} />
                    )}

                    {/* Optional Attachments */}
                    {optionalAttachments.length > 0 && (
                        <LabelTextItem label={t("change.optionalAttachments")} text={formatAttachments(optionalAttachments)} />
                    )}
                </Loading>
            </ContentBox>
        </>
    );
};
